import React, {useCallback, useMemo} from 'react';


import {GroupSuggest} from 'src/components/group-suggest';
import {DateInput} from 'src/components/input';
import {ResponsiveElementGroup} from 'src/components/responsive-element-group';
import {SmartSection} from 'src/components/smart-section';
import {UserStatusSuggest} from 'src/components/user-status-suggest';
import {UserTypeSuggest} from 'src/components/user-type-suggest';
import {LICENSED_USER_TYPE_CODE, NO_ACCESS_USER_TYPE_CODE} from 'src/components/user-type-suggest/user-type-suggest';
import {UserStatus as UserStatuses} from 'src/openapi/admin/models/UserStatus';

import {CheckBox} from '@pb/kit-component-checkbox';
import {FormSection} from '@pb/kit-component-form-section';
import {Group} from '@pb/kit-component-group';
import {Info} from '@pb/kit-component-info';
import Label from '@pb/kit-component-label';

import {UserAccountInformationSkeleton} from './user-account-information.skeleton';

import type {UserInformation} from 'src/modules/profile/account/create-user/typedef';
import type {AccountGroup} from 'src/openapi/admin/models/AccountGroup';
import type {CheckBoxChangeData} from 'src/typedef';

type Props = {
    isLoading: boolean,
    userAccountInformation: UserInformation,
    availableStatuses: string[] | null,
    setUserAccountInformation: React.Dispatch<React.SetStateAction<UserInformation>>,
    disabled?: boolean,
    userId?: number,
    accountId: number,
    onFieldChange: (fieldName: string) => void,
};

export const UserAccountInformation = (props: Props) => {
    const {
        userAccountInformation,
        availableStatuses,
        setUserAccountInformation,
        isLoading,
        disabled,
        accountId,
        onFieldChange,
    } = props;

    const onGroupChange = useCallback((group: AccountGroup | null) => {
        onFieldChange('Group');
        setUserAccountInformation(prevState => ({...prevState, group}));
    }, [onFieldChange, setUserAccountInformation]);

    const changeExpirationDate = useCallback((expirationDate: string) => {
        setUserAccountInformation(prevState => {
            if (prevState.expirationDate !== expirationDate) {
                onFieldChange('Status Expiration Date');
            }

            return {...prevState, expirationDate};
        });
    }, [setUserAccountInformation, onFieldChange]);

    const onUserTypeChange = useCallback((typeCode: string) => {
        onFieldChange('User type');
        setUserAccountInformation(prevState => {
            let statusCode = prevState.statusCode;

            if (typeCode === NO_ACCESS_USER_TYPE_CODE && statusCode !== UserStatuses.EMPTY) {
                statusCode = UserStatuses.EMPTY;
            } else if (
                typeCode !== NO_ACCESS_USER_TYPE_CODE &&
                statusCode === UserStatuses.EMPTY &&
                availableStatuses
            ) {
                statusCode = availableStatuses[0];
            }

            return {...prevState, typeCode, statusCode};
        });
    }, [availableStatuses, onFieldChange, setUserAccountInformation]);

    const onUserStatusChange = useCallback((statusCode: string) => {
        onFieldChange('User status');
        setUserAccountInformation(prevState => {
            let typeCode = prevState.typeCode;

            if (statusCode === UserStatuses.EMPTY && typeCode !== NO_ACCESS_USER_TYPE_CODE) {
                typeCode = NO_ACCESS_USER_TYPE_CODE;
            } else if (statusCode !== UserStatuses.EMPTY && typeCode === NO_ACCESS_USER_TYPE_CODE) {
                typeCode = LICENSED_USER_TYPE_CODE;
            }
            return {...prevState, typeCode, statusCode};
        });
    }, [onFieldChange, setUserAccountInformation]);

    const changeTemplateUser = useCallback((changeData: CheckBoxChangeData) => {
        onFieldChange('Template user');
        setUserAccountInformation(prevState => ({
            ...prevState,
            templateUser: changeData.event.currentTarget.checked,
        }));
    }, [onFieldChange, setUserAccountInformation]);

    const popover = useMemo(() => (
        <span>
            This option enables to mark the user as a template and use the configurations of
            newsletters, searches, lists, email alerts, layout settings during the creation
            of new users. The configurations will be applied in case the user is selected in
            the <b>Create new users based on user*</b> in the Account Details
            or in the <b>Import alerts & layouts from user</b> in the Bulk Import of users.
        </span>
    ), []);

    return (
        <SmartSection
            isLoading={isLoading || !availableStatuses}
            hasLoadingError={false}
            skeletonView={<UserAccountInformationSkeleton/>}>
            <FormSection headline="User Account Information">
                <div style={{maxWidth: 1050}}>
                    <ResponsiveElementGroup>
                        {availableStatuses ? (
                            <div style={{width: 300}}>
                                <Label>Status</Label>
                                <UserStatusSuggest
                                    disabled={disabled}
                                    accountId={accountId}
                                    availableStatuses={availableStatuses}
                                    userStatus={userAccountInformation.statusCode}
                                    setUserStatus={onUserStatusChange}
                                />
                            </div>
                        ) : null}
                        <div style={{width: 300}}>
                            <Label>Status Expiration Date</Label>
                            <DateInput
                                id="status-expiration-date-picker"
                                clearable={true}
                                selectedDate={userAccountInformation.expirationDate || ''}
                                onChange={changeExpirationDate}
                                readonly={disabled}
                            />
                        </div>
                        <div style={{width: 300}}>
                            <Label htmlFor="account-group-suggest">Group</Label>
                            <GroupSuggest
                                id="account-group-suggest"
                                disabled={disabled}
                                placeholder="Select group"
                                width="100%"
                                selectedGroupId={userAccountInformation.group?.id || null}
                                accountId={accountId}
                                onSelect={onGroupChange}
                            />
                        </div>
                        <div style={{width: 300}}>
                            <Label>User type</Label>
                            <UserTypeSuggest
                                disabled={disabled}
                                userType={userAccountInformation.typeCode}
                                setUserType={onUserTypeChange}
                                excludeNoAccess={!availableStatuses?.includes(UserStatuses.EMPTY)}
                            />
                        </div>
                        <Group direction="v" gap="s" style={{width: 300}}>
                            <Label>
                                <Info
                                    dropdownProps={{
                                        style: {
                                            width: '265px',
                                        },
                                    }}
                                    type={{popover}}>
                                    Preferences
                                </Info>
                            </Label>
                            <CheckBox
                                disabled={disabled}
                                label={'This is a "template" user of a client'}
                                checked={userAccountInformation.templateUser}
                                onCheckBoxChange={changeTemplateUser}
                            />
                        </Group>
                    </ResponsiveElementGroup>
                </div>
            </FormSection>
        </SmartSection>
    );
};
